import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Eye, EyeOff, Users, Shield, UserCheck } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { LoadingButton } from '../components/ui/LoadingButton'
import { Badge } from '../components/ui/Badge'
import { useAuthStore } from '../stores/authStore'
import { apiClient } from '../services/api'
import { Role } from '../types/api'
import toast from 'react-hot-toast'

interface User {
  id: string
  username: string
  full_name: string
  email: string
  phone?: string
  role_id: string
  role_name?: string
  is_active: boolean
  last_login_at?: string
  created_at: string
}

interface CreateUserForm {
  full_name: string
  email: string
  phone: string
  password: string
  confirmPassword: string
  role_id: string
}

const roleColors = {
  admin: 'destructive',
  manager: 'warning',
  cashier: 'success'
} as const

const roleIcons = {
  admin: Shield,
  manager: UserCheck,
  cashier: Users
}

export const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { user: currentUser } = useAuthStore()

  const [createForm, setCreateForm] = useState<CreateUserForm>({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role_id: ''
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersResponse, rolesResponse] = await Promise.all([
        apiClient.getUsers(),
        apiClient.getRoles()
      ])
      setUsers(usersResponse.users || [])
      setRoles(rolesResponse.roles || [])

      // Set default role_id if roles exist and form is empty
      if (rolesResponse.roles && rolesResponse.roles.length > 0 && !createForm.role_id) {
        setCreateForm(prev => ({ ...prev, role_id: rolesResponse.roles[0].id }))
      }
    } catch (error) {
      toast.error('Failed to fetch data')
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setCreateForm({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role_id: roles.length > 0 ? roles[0].id : ''
    })
    setShowPassword(false)
    setShowConfirmPassword(false)
    setEditingUser(null)
  }

  const validateForm = (): boolean => {
    if (!createForm.full_name.trim()) {
      toast.error('Full name is required')
      return false
    }
    
    if (!createForm.email.trim()) {
      toast.error('Email is required')
      return false
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(createForm.email)) {
      toast.error('Please enter a valid email address')
      return false
    }
    
    if (!editingUser) {
      if (!createForm.password) {
        toast.error('Password is required')
        return false
      }
      
      if (createForm.password.length < 6) {
        toast.error('Password must be at least 6 characters')
        return false
      }
      
      if (createForm.password !== createForm.confirmPassword) {
        toast.error('Passwords do not match')
        return false
      }
    }
    
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setFormLoading(true)
    
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          full_name: createForm.full_name,
          email: createForm.email,
          phone: createForm.phone,
          role_id: createForm.role_id
        }

        await apiClient.updateUser(editingUser.id, updateData)
        toast.success('User updated successfully')
      } else {
        // Create user
        const userData = {
          full_name: createForm.full_name,
          email: createForm.email,
          phone: createForm.phone,
          password: createForm.password,
          role_id: createForm.role_id
        }

        await apiClient.createUser(userData)
        toast.success('User created successfully')
      }

      resetForm()
      setShowCreateForm(false)
      fetchData()
    } catch (error: any) {
      toast.error(error.message || `Failed to ${editingUser ? 'update' : 'create'} user`)
    } finally {
      setFormLoading(false)
    }
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setCreateForm({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      password: '',
      confirmPassword: '',
      role_id: user.role_id || (roles.length > 0 ? roles[0].id : '')
    })
    setShowCreateForm(true)
  }

  const handleToggleStatus = async (user: User) => {
    try {
      await apiClient.updateUser(user.id, { is_active: !user.is_active })
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`)
      fetchData()
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user status')
    }
  }

  const handleDelete = async (user: User) => {
    if (!confirm(`Are you sure you want to delete ${user.full_name}?`)) return

    try {
      await apiClient.deleteUser(user.id)
      toast.success('User deleted successfully')
      fetchData()
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete user')
    }
  }

  const getRoleBadge = (roleName: string) => {
    const roleKey = roleName.toLowerCase()
    const color = roleColors[roleKey as keyof typeof roleColors] || 'secondary'
    const Icon = roleIcons[roleKey as keyof typeof roleIcons] || Shield

    return (
      <Badge variant={color} className="capitalize">
        <Icon className="w-3 h-3 mr-1" />
        {roleName}
      </Badge>
    )
  }

  const canManageUser = (user: User) => {
    if (currentUser?.role !== 'admin') return false
    if (user.id === currentUser?.id) return false // Can't manage self
    return true
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage system users and their roles</p>
        </div>
        
        {currentUser?.role === 'admin' && (
          <Button
            onClick={() => {
              resetForm()
              setShowCreateForm(true)
            }}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add User
          </Button>
        )}
      </div>

      {/* Create/Edit User Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingUser ? 'Edit User' : 'Create New User'}</CardTitle>
            <CardDescription>
              {editingUser ? 'Update user information' : 'Add a new user to the system'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={createForm.full_name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Role *
                  </label>
                  <select
                    value={createForm.role_id}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, role_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700"
                    required
                  >
                    <option value="">Select a role</option>
                    {roles.filter(role => role.is_active).map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name} {role.is_system ? '(System)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {!editingUser && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={createForm.password}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Enter password"
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={createForm.confirmPassword}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          placeholder="Confirm password"
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <LoadingButton
                  type="submit"
                  isLoading={formLoading}
                  loadingText={editingUser ? "Updating..." : "Creating..."}
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </LoadingButton>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetForm()
                    setShowCreateForm(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>System Users ({users.length})</CardTitle>
          <CardDescription>
            Manage users and their access levels
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">User</th>
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Contact</th>
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Role</th>
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-300">Last Login</th>
                    {currentUser?.role === 'admin' && (
                      <th className="text-right p-3 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{user.full_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">@{user.username}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{user.email}</p>
                          {user.phone && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">{user.phone}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {getRoleBadge(user.role_name || 'Unknown')}
                      </td>
                      <td className="p-3">
                        <Badge variant={user.is_active ? 'success' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {user.last_login_at 
                            ? new Date(user.last_login_at).toLocaleDateString()
                            : 'Never'
                          }
                        </p>
                      </td>
                      {currentUser?.role === 'admin' && (
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-2">
                            {canManageUser(user) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(user)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleStatus(user)}
                                  className={user.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                                >
                                  {user.is_active ? 'Deactivate' : 'Activate'}
                                </Button>
                                
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(user)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}